import React from 'react';
import PartnerPageTemplate from './PartnerPageTemplate';
import {
  Droplet,
  Repeat,
  Clock,
  Users,
  HeartPulse,
  ShieldCheck,
  FileCheck,
  MapPin,
  Phone,
  Calendar,
} from 'lucide-react';

const CANONICAL = 'https://www.moveracaremobility.com/healthcare-partners/dialysis-centers';

const DialysisCenters = () => {
  const seo = {
    title: 'Dialysis Transportation | Recurring NEMT for Dialysis Centers | MoveraCare',
    description: 'Recurring wheelchair and ambulatory dialysis transportation for centers across Greater Houston. Reliable standing schedules, trained drivers, and consistent on-time performance.',
    canonical: CANONICAL,
    schemaId: 'partner-dialysis-schema',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Dialysis Transportation',
      serviceType: 'Non-Emergency Medical Transportation for Dialysis',
      areaServed: 'Greater Houston, TX',
      provider: {
        '@type': 'MedicalBusiness',
        name: 'MoveraCare Mobility',
        telephone: '+1-832-925-2128',
        url: 'https://www.moveracaremobility.com',
      },
      audience: {
        '@type': 'MedicalAudience',
        audienceType: 'Dialysis Centers, Nephrology Clinics',
      },
    },
  };

  return (
    <PartnerPageTemplate
      testid="partner-dialysis-centers"
      seo={seo}
      eyebrow="DIALYSIS CENTERS"
      heading="Recurring Dialysis Transportation Patients &amp; Centers Can Rely On"
      intro="MoveraCare Mobility provides recurring wheelchair and ambulatory transportation for dialysis patients across Greater Houston — supporting consistent on-time treatment attendance and predictable clinic scheduling."
      audience={{
        title: 'For Dialysis Center Teams & Patient Care Coordinators',
        description: 'This page is for dialysis center administrators, patient care coordinators, and social workers responsible for helping patients maintain a reliable treatment schedule.',
        bullets: [
          'In-center hemodialysis clinics',
          'Home dialysis and peritoneal dialysis coordination teams',
          'Nephrology practice and clinic coordinators',
          'Patient care coordinators and social workers',
          'Dialysis center administrators and operations managers',
          'Case managers coordinating dialysis benefits',
        ],
      }}
      problem={{
        title: 'Missed Rides Mean Missed Treatments',
        description: 'Dialysis schedules are rigid. Missed or late transportation means missed treatment, clinical decline, and disrupted center operations. MoveraCare is built to support consistent recurring dialysis transportation.',
        bullets: [
          'Reliable standing schedules for 3-day-per-week treatment routines',
          'Wheelchair and ambulatory options based on patient mobility',
          'Wait-and-return or same-day return coordination',
          'Consistent driver familiarity supporting patient comfort and dignity',
          'Operational communication with dialysis center staff',
        ],
      }}
      capabilities={{
        title: 'Dialysis-Focused Transportation Capabilities',
        items: [
          { icon: Repeat, title: 'Recurring Standing Schedules', desc: 'Standing wheelchair and ambulatory transportation for consistent weekly dialysis treatment routines.' },
          { icon: Droplet, title: 'Pre- and Post-Treatment Transport', desc: 'Structured pickup and return trips supporting patient stability before and after dialysis.' },
          { icon: Clock, title: 'On-Time Consistency', desc: 'Recurring scheduling built for the punctuality that treatment attendance requires.' },
          { icon: HeartPulse, title: 'Wheelchair & Ambulatory Support', desc: 'Wheelchair-accessible vans and ambulatory transport; drivers assist passengers during transport.' },
          { icon: Users, title: 'Driver Familiarity', desc: 'Consistent driver assignments where possible for patient comfort and continuity.' },
          { icon: Calendar, title: 'Schedule Coordination', desc: 'Coordination with clinic scheduling teams to reduce disruption and align windows.' },
        ],
      }}
      coordination={{
        title: 'Setting Up Recurring Dialysis Transportation',
        steps: [
          { title: 'Patient Profile', desc: 'Center or coordinator shares patient mobility level, address, treatment schedule, and any authorizations.' },
          { title: 'Standing Schedule', desc: 'MoveraCare configures a standing schedule aligned to treatment days and appointment windows.' },
          { title: 'Ongoing Transport', desc: 'Recurring pickups and returns run on a consistent schedule with operational communication.' },
          { title: 'Adjustments', desc: 'Center or patient can request schedule changes or ad-hoc trips through dispatch as needed.' },
        ],
      }}
      why={{
        title: 'Why Dialysis Centers Choose MoveraCare',
        items: [
          { icon: Repeat, title: 'Built for Recurring Care', desc: 'Operations designed around standing weekly schedules — not one-off trips.' },
          { icon: Clock, title: 'Treatment-Aligned Punctuality', desc: 'Focus on the on-time performance that dialysis schedules require.' },
          { icon: HeartPulse, title: 'Wheelchair & Ambulatory Fleet', desc: 'Wheelchair-accessible vans and ambulatory vehicles for varied patient needs.' },
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious Communication', desc: 'Professional handling of patient information across dispatch and operations.' },
          { icon: MapPin, title: 'Greater Houston Coverage', desc: 'Verified service area supporting Fort Bend and surrounding Greater Houston counties.' },
        ],
      }}
      transportRequest={{
        title: 'Set Up Recurring Dialysis Transportation',
        description: 'Center coordinators, social workers, and patient care teams can request recurring transportation setups directly through the Request Patient Transportation form or via dispatch.',
        cta: { label: 'Request Recurring Transportation', to: '/request-patient-transportation' },
      }}
      vendorRelationship={{
        title: 'Begin a Center Partnership or Provider Relationship',
        description: 'Dialysis center administrators and network teams can access MoveraCare’s vendor documentation packet or contact Provider Relations to formalize a recurring transportation partnership.',
      }}
    />
  );
};

export default DialysisCenters;
